import React, { useRef } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ArrowLeftRight,
  Italic,
  Trash2,
} from 'lucide-react';
import type { CampaignHeroData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import FontColorPicker from './FontColorPicker';
import { FONT_FAMILY_OPTIONS } from './fontFamilyOptions';
import styles from './CampaignHeroSection.module.scss';

type TextAlignOption = 'left' | 'center' | 'right';
type BodyTextAlignOption = 'left' | 'center' | 'right' | 'justify';

function hexToRgba(hex: string, alpha: number): string | null {
  const raw = hex.trim().replace(/^#/, '');
  if (raw.length === 3) {
    const r = Number.parseInt(raw[0] + raw[0], 16);
    const g = Number.parseInt(raw[1] + raw[1], 16);
    const b = Number.parseInt(raw[2] + raw[2], 16);
    if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))
      return null;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (raw.length !== 6) return null;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))
    return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CampaignHeroSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): CampaignHeroData {
  const rawTitleAlign = data.titleAlign;
  const rawBodyAlign = data.bodyAlign;
  const rawSwapFrames = data.swapFrames;

  return {
    imageUrl: typeof data.imageUrl === 'string' ? data.imageUrl : '',
    title:
      typeof data.title === 'string'
        ? data.title
        : 'A Ministry Built on Salvation',
    bodyText:
      typeof data.bodyText === 'string'
        ? data.bodyText
        : 'Write a paragraph that talks about your company here.',
    backgroundColor:
      typeof data.backgroundColor === 'string'
        ? data.backgroundColor
        : '#4b2c1a',
    backgroundOverlay:
      typeof data.backgroundOverlay === 'boolean'
        ? data.backgroundOverlay
        : false,
    titleFontFamily:
      typeof data.titleFontFamily === 'string' ? data.titleFontFamily : '',
    titleFontSize:
      typeof data.titleFontSize === 'number' && data.titleFontSize > 0
        ? data.titleFontSize
        : 48,
    titleColor: typeof data.titleColor === 'string' ? data.titleColor : '',
    titleBold: typeof data.titleBold === 'boolean' ? data.titleBold : true,
    titleItalic:
      typeof data.titleItalic === 'boolean' ? data.titleItalic : false,
    titleAlign:
      rawTitleAlign === 'left' ||
      rawTitleAlign === 'center' ||
      rawTitleAlign === 'right'
        ? rawTitleAlign
        : 'left',
    bodyFontFamily:
      typeof data.bodyFontFamily === 'string' ? data.bodyFontFamily : '',
    bodyFontSize:
      typeof data.bodyFontSize === 'number' && data.bodyFontSize > 0
        ? data.bodyFontSize
        : 24,
    bodyColor: typeof data.bodyColor === 'string' ? data.bodyColor : '',
    bodyBold: typeof data.bodyBold === 'boolean' ? data.bodyBold : false,
    bodyItalic: typeof data.bodyItalic === 'boolean' ? data.bodyItalic : false,
    bodyAlign:
      rawBodyAlign === 'left' ||
      rawBodyAlign === 'center' ||
      rawBodyAlign === 'right' ||
      rawBodyAlign === 'justify'
        ? rawBodyAlign
        : 'left',
    swapFrames: typeof rawSwapFrames === 'boolean' ? rawSwapFrames : false,
  };
}

export function CampaignHeroSectionEditor({
  data,
  onUpdateData,
}: CampaignHeroSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const update = (patch: Partial<CampaignHeroData>) => {
    onUpdateData({ ...data, ...patch });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;

    // Keep payload small enough for localStorage persistence.
    compressImageFile(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.72 })
      .then((imageUrl) => {
        update({ imageUrl });
      })
      .catch(() => {});

    e.target.value = '';
  };

  const clearImage = () => {
    update({ imageUrl: '' });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
        aria-label="Upload campaign hero image"
      />

      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Section background</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Color
            <FontColorPicker
              value={d.backgroundColor ?? ''}
              onChange={(color) =>
                update({ backgroundColor: color || '#4b2c1a' })
              }
              panelPlacement="bottom"
            />
          </label>
        </div>

        <div className={styles.fontGroupRow} style={{ marginTop: '0.5rem' }}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={Boolean(d.backgroundOverlay)}
              onChange={(e) => update({ backgroundOverlay: e.target.checked })}
              className={styles.checkboxInput}
            />{' '}
            (semi-transparent)
          </label>
        </div>
      </div>

      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Left title box</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font
            <select
              className={styles.fontSelect}
              value={d.titleFontFamily}
              onChange={(e) => update({ titleFontFamily: e.target.value })}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size (px)
            <input
              type="number"
              min={20}
              max={84}
              className={styles.fontInput}
              value={d.titleFontSize}
              onChange={(e) =>
                update({ titleFontSize: Number(e.target.value) || 48 })
              }
            />
          </label>
        </div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={d.titleColor ?? ''}
            onChange={(color) => update({ titleColor: color })}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.titleBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ titleBold: !d.titleBold })}
            aria-label="Toggle title bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.titleItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ titleItalic: !d.titleItalic })}
            aria-label="Toggle title italic"
          >
            <Italic size={12} />
          </button>
          {(['left', 'center', 'right'] as TextAlignOption[]).map((align) => (
            <button
              key={`title-${align}`}
              type="button"
              className={`${styles.toolbarBtn} ${d.titleAlign === align ? styles.toolbarBtnActive : ''}`}
              onClick={() => update({ titleAlign: align })}
              aria-label={`Set title alignment to ${align}`}
            >
              {align === 'left' ? (
                <AlignLeft size={12} />
              ) : align === 'center' ? (
                <AlignCenter size={12} />
              ) : (
                <AlignRight size={12} />
              )}
            </button>
          ))}
        </div>
        <textarea
          className={styles.titleInput}
          value={d.title ?? ''}
          onChange={(e) => update({ title: e.target.value })}
          rows={2}
          placeholder="Left title"
        />
      </div>

      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Left image (under title)</div>
        <div className={styles.editorRow}>
          <div className={styles.previewThumb}>
            <img
              src={d.imageUrl?.trim() ? d.imageUrl : galleryPlaceholder}
              alt=""
            />
          </div>

          <div className={styles.imageControlsRow}>
            <input
              type="text"
              className={styles.imageUrlInput}
              value={d.imageUrl ?? ''}
              onChange={(e) => update({ imageUrl: e.target.value })}
              placeholder="Image URL"
            />
            <button
              type="button"
              className={styles.browseBtn}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse
            </button>
            <button
              type="button"
              className={styles.clearBtn}
              onClick={clearImage}
              disabled={!d.imageUrl?.trim()}
              aria-label="Clear left image"
              title="Clear image"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.toolbarRow}>
        <button
          type="button"
          className={`${styles.toolbarBtn} ${
            d.swapFrames ? styles.toolbarBtnActive : ''
          }`}
          onClick={() => update({ swapFrames: !d.swapFrames })}
          aria-label="Switch campaign hero frames"
          title="Switch Frame 1 and Frame 2"
        >
          <ArrowLeftRight size={12} />
        </button>
      </div>

      <div className={styles.fontGroup}>
        <div className={styles.fontGroupTitle}>Right text box</div>
        <div className={styles.fontGroupRow}>
          <label className={styles.fontLabel}>
            Font
            <select
              className={styles.fontSelect}
              value={d.bodyFontFamily}
              onChange={(e) => update({ bodyFontFamily: e.target.value })}
            >
              {FONT_FAMILY_OPTIONS.map((option) => (
                <option key={`body-${option.label}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fontLabel}>
            Size (px)
            <input
              type="number"
              min={14}
              max={52}
              step={1}
              className={styles.fontInput}
              value={d.bodyFontSize}
              onChange={(e) =>
                update({ bodyFontSize: Number(e.target.value) || 24 })
              }
            />
          </label>
        </div>
        <div className={styles.toolbarRow}>
          <FontColorPicker
            value={d.bodyColor ?? ''}
            onChange={(color) => update({ bodyColor: color })}
            compact
          />
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.bodyBold ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ bodyBold: !d.bodyBold })}
            aria-label="Toggle right text bold"
          >
            <Bold size={12} />
          </button>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${d.bodyItalic ? styles.toolbarBtnActive : ''}`}
            onClick={() => update({ bodyItalic: !d.bodyItalic })}
            aria-label="Toggle right text italic"
          >
            <Italic size={12} />
          </button>
          {(
            ['left', 'center', 'right', 'justify'] as BodyTextAlignOption[]
          ).map((align) => (
            <button
              key={`body-${align}`}
              type="button"
              className={`${styles.toolbarBtn} ${d.bodyAlign === align ? styles.toolbarBtnActive : ''}`}
              onClick={() => update({ bodyAlign: align })}
              aria-label={`Set right text alignment to ${align}`}
            >
              {align === 'left' ? (
                <AlignLeft size={12} />
              ) : align === 'center' ? (
                <AlignCenter size={12} />
              ) : align === 'justify' ? (
                <AlignJustify size={12} />
              ) : (
                <AlignRight size={12} />
              )}
            </button>
          ))}
        </div>
        <textarea
          className={styles.bodyInput}
          value={d.bodyText ?? ''}
          onChange={(e) => update({ bodyText: e.target.value })}
          rows={5}
          placeholder="Right side text"
        />
      </div>
    </>
  );
}

interface CampaignHeroSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function CampaignHeroSection({ data }: CampaignHeroSectionProps) {
  const d = getData(data);
  const imageSrc = d.imageUrl?.trim() ? d.imageUrl : galleryPlaceholder;

  const titleStyle: React.CSSProperties = {
    fontSize: `${d.titleFontSize ?? 48}px`,
    fontWeight: d.titleBold ? 700 : 400,
    fontStyle: d.titleItalic ? 'italic' : 'normal',
    textAlign: d.titleAlign ?? 'left',
    ...(d.titleColor ? { color: d.titleColor } : {}),
    ...(d.titleFontFamily ? { fontFamily: d.titleFontFamily } : {}),
  };

  const bodyStyle: React.CSSProperties = {
    fontSize: `${d.bodyFontSize ?? 24}px`,
    fontWeight: d.bodyBold ? 700 : 400,
    fontStyle: d.bodyItalic ? 'italic' : 'normal',
    textAlign: d.bodyAlign ?? 'left',
    ...(d.bodyColor ? { color: d.bodyColor } : {}),
    ...(d.bodyFontFamily ? { fontFamily: d.bodyFontFamily } : {}),
  };

  const leftFrame = (
    <div className={styles.leftFrame}>
      <h2 className={styles.titleBox} style={titleStyle}>
        {d.title}
      </h2>
      <div className={styles.imageBox}>
        <img src={imageSrc} alt="" className={styles.image} />
      </div>
    </div>
  );

  const rightFrame = (
    <div className={styles.rightFrame}>
      <p className={styles.bodyText} style={bodyStyle}>
        {d.bodyText}
      </p>
    </div>
  );

  return (
    <section
      className={styles.section}
      style={{
        backgroundColor: d.backgroundOverlay
          ? (hexToRgba(d.backgroundColor ?? '#4b2c1a', 0.78) ??
            d.backgroundColor ??
            '#4b2c1a')
          : (d.backgroundColor ?? '#4b2c1a'),
      }}
    >
      <div className={styles.layout}>
        {d.swapFrames ? (
          <>
            {rightFrame}
            {leftFrame}
          </>
        ) : (
          <>
            {leftFrame}
            {rightFrame}
          </>
        )}
      </div>
    </section>
  );
}

export default CampaignHeroSection;
